/**
 * VocalForge™ Extreme Vocals Taxonomy
 * 
 * Scream types and extreme vocal techniques with physics-based descriptors
 * for AI music generation. Each type includes behavioral tags that describe
 * what the sound DOES rather than technical DSP parameters.
 */

export interface ScreamType {
  id: string;
  label: string;
  category: 'fry' | 'false_chord' | 'guttural' | 'hybrid' | 'specialty';
  description: string;
  
  // Physics-based behavioral descriptors
  physicsProfile: {
    cordEngagement: string;      // How vocal cords interact
    airflowPattern: string;      // Breath mechanics
    resonanceChamber: string;    // Where sound resonates
    frequencyFocus: string;      // Dominant frequency range
    attackCharacter: string;     // How notes begin
    sustainBehavior: string;     // How notes are held
    releasePattern: string;      // How notes end
  };
  
  // Trait influence (how this scream affects DerivedTraits)
  traitModifiers: {
    grit: number;        // -50 to +50 modifier
    rasp: number;
    projection: number;
    airiness: number;
    nasality: number;
    lowEnd: number;
    speed: number;
    cleanness: number;
  };
  
  // Suno-compatible style tags
  sunoTags: string[];
  
  // What to explicitly exclude when using this technique
  excludeTags: string[];
  
  // Best use cases
  bestFor: string[];
  
  // Physics prompt block for LAYER-1
  physicsPrompt: string;
}

export interface ScreamIntensity {
  id: string;
  label: string;
  modifier: number;  // Multiplier for trait modifiers (0.5 = half intensity, 2 = double)
  description: string;
}

// ============================================================================
// INTENSITY LEVELS
// ============================================================================

export const SCREAM_INTENSITIES: ScreamIntensity[] = [
  {
    id: 'subtle',
    label: 'Subtle / Texture',
    modifier: 0.3,
    description: 'Light touch - adds texture without dominating. Good for layering.'
  },
  {
    id: 'moderate',
    label: 'Moderate',
    modifier: 0.6,
    description: 'Clear presence but controlled. Maintains lyrical clarity.'
  },
  {
    id: 'full',
    label: 'Full Intensity',
    modifier: 1.0,
    description: 'Standard execution of the technique at natural power.'
  },
  {
    id: 'extreme',
    label: 'Extreme / Unhinged',
    modifier: 1.5,
    description: 'Maximum aggression. May sacrifice clarity for impact.'
  }
];

// ============================================================================
// SCREAM TYPE DEFINITIONS
// ============================================================================

export const SCREAM_TYPES: ScreamType[] = [
  // -------------------------------------------------------------------------
  // FRY CATEGORY
  // -------------------------------------------------------------------------
  {
    id: 'fry_scream',
    label: 'Fry Scream',
    category: 'fry',
    description: 'High-pitched scream using vocal fry technique. Controlled distortion through arytenoid cartilage compression.',
    physicsProfile: {
      cordEngagement: 'Arytenoid cartilage compression with controlled vocal fry activation',
      airflowPattern: 'High-pressure sustained airflow with diaphragm lock',
      resonanceChamber: 'Pharyngeal cavity emphasis with nasal routing',
      frequencyFocus: '1kHz-4kHz primary, 6kHz-10kHz harmonic shimmer',
      attackCharacter: 'Sharp glottal onset with immediate fry engagement',
      sustainBehavior: 'Consistent fry crackle maintaining pitch center',
      releasePattern: 'Controlled fade with maintained fry texture'
    },
    traitModifiers: { grit: 40, rasp: 35, projection: 30, airiness: -20, nasality: 25, lowEnd: -15, speed: 10, cleanness: -45 },
    sunoTags: ['fry scream', 'metalcore vocals', 'high scream', 'aggressive vocals', 'distorted vocals'],
    excludeTags: ['clean vocals', 'smooth', 'soft', 'whisper', 'operatic'],
    bestFor: ['Choruses', 'Breakdowns', 'Climactic moments'],
    physicsPrompt: `{SCREAM-PHYSICS: FRY}
[Scream_Behavior: Arytenoid-compression fry activation; High-pressure diaphragm lock]
[Scream_Frequencies: 1kHz-4kHz fundamental; 6kHz-10kHz harmonic shimmer layer]
[Scream_Dynamics: Sharp glottal attack; Sustained fry crackle; Controlled textured release]
[Scream_Vocal_State: Compressed-vibration with controlled chaos harmonics]`
  },
  {
    id: 'fry_inhale',
    label: 'Fry Scream (Inhale Variant)',
    category: 'fry',
    description: 'Inhaled version of fry scream. Creates otherworldly, demonic quality.',
    physicsProfile: {
      cordEngagement: 'Inverted cord tension with reversed fry mechanism',
      airflowPattern: 'Controlled inhale with diaphragm expansion',
      resonanceChamber: 'Throat cavity creates negative pressure resonance',
      frequencyFocus: '800Hz-3kHz with ghostly overtone series',
      attackCharacter: 'Gasping onset with rapid fry engagement',
      sustainBehavior: 'Wavering pitch with organic flutter',
      releasePattern: 'Abrupt cutoff or fade to breath sound'
    },
    traitModifiers: { grit: 35, rasp: 45, projection: 15, airiness: 30, nasality: 10, lowEnd: -25, speed: 20, cleanness: -50 },
    sunoTags: ['inhale scream', 'demonic vocals', 'inverted scream', 'horror vocals'],
    excludeTags: ['clean', 'melodic', 'smooth', 'warm'],
    bestFor: ['Intros', 'Transitions', 'Horror elements'],
    physicsPrompt: `{SCREAM-PHYSICS: FRY-INHALE}
[Scream_Behavior: Reversed airflow fry mechanism; Inverted cord tension]
[Scream_Resonance: Negative-pressure throat resonance]
[Scream_Frequencies: 800Hz-3kHz fundamental with ghostly overtone flutter]
[Scream_Dynamics: Gasping attack; Wavering sustained pitch; Abrupt cutoff]`
  },

  // -------------------------------------------------------------------------
  // FALSE CHORD / GRIT CATEGORY
  // -------------------------------------------------------------------------
  {
    id: 'false_chord',
    label: 'False Chord / Grit',
    category: 'false_chord',
    description: 'Mid-range aggressive scream using false vocal folds. Versatile with good lyrical clarity.',
    physicsProfile: {
      cordEngagement: 'False vocal fold vibration layered over true cord phonation',
      airflowPattern: 'Moderate-high pressure with rhythmic diaphragm pulses',
      resonanceChamber: 'Oral cavity emphasis with chest support',
      frequencyFocus: '300Hz-2kHz primary with 3kHz-5kHz grit overtones',
      attackCharacter: 'Punchy onset with immediate false fold activation',
      sustainBehavior: 'Dual-membrane vibration maintaining pitch and grit',
      releasePattern: 'Can fade clean or maintain grit to cutoff'
    },
    traitModifiers: { grit: 45, rasp: 30, projection: 35, airiness: -10, nasality: 5, lowEnd: 15, speed: 15, cleanness: -40 },
    sunoTags: ['false chord', 'gritty vocals', 'metal scream', 'aggressive midrange', 'hardcore vocals'],
    excludeTags: ['clean pop', 'smooth jazz', 'soft', 'ethereal'],
    bestFor: ['Verses', 'Choruses', 'Sustained passages'],
    physicsPrompt: `{SCREAM-PHYSICS: FALSE-CHORD}
[Scream_Behavior: Dual-membrane vibration; False vocal folds layered over true cord phonation]
[Scream_Frequencies: 300Hz-2kHz fundamental; 3kHz-5kHz grit harmonic layer]
[Scream_Dynamics: Punchy attack; Sustained dual-vibration; Flexible release]`
  },

  // -------------------------------------------------------------------------
  // GUTTURAL CATEGORY
  // -------------------------------------------------------------------------
  {
    id: 'guttural',
    label: 'Guttural',
    category: 'guttural',
    description: 'Deep, bowel-shaking low-frequency scream. Subharmonic generation through extreme cord compression.',
    physicsProfile: {
      cordEngagement: 'Maximum cord compression generating subharmonics',
      airflowPattern: 'Explosive diaphragm pressure bursts',
      resonanceChamber: 'Deep chest cavity and throat expansion',
      frequencyFocus: 'Sub-100Hz fundamental with 150Hz-400Hz body',
      attackCharacter: 'Percussive blast onset',
      sustainBehavior: 'Rumbling sustained texture with pitch instability',
      releasePattern: 'Abrupt stop or growl decay'
    },
    traitModifiers: { grit: 50, rasp: 20, projection: 40, airiness: -30, nasality: -20, lowEnd: 50, speed: -10, cleanness: -50 },
    sunoTags: ['guttural', 'death metal vocals', 'brutal vocals', 'deep growl', 'subharmonic vocals'],
    excludeTags: ['high pitched', 'clean', 'melodic', 'bright', 'airy'],
    bestFor: ['Brutal sections', 'Breakdowns', 'Impact moments'],
    physicsPrompt: `{SCREAM-PHYSICS: GUTTURAL}
[Scream_Behavior: Maximum cord compression; Subharmonic generation; Explosive diaphragm bursts]
[Scream_Frequencies: Sub-100Hz fundamental; 150Hz-400Hz body mass]
[Scream_Dynamics: Percussive blast attack; Rumbling sustained texture; Abrupt stop]`
  },
  {
    id: 'low_growl',
    label: 'Low Growl',
    category: 'guttural',
    description: 'Deep growl with limited lyrical legibility. Cookie monster territory.',
    physicsProfile: {
      cordEngagement: 'Loose cord vibration with heavy false fold engagement',
      airflowPattern: 'Steady low-pressure sustained flow',
      resonanceChamber: 'Expanded throat with chest resonance',
      frequencyFocus: '80Hz-250Hz dominant',
      attackCharacter: 'Soft onset building to full growl',
      sustainBehavior: 'Continuous rumble with organic variation',
      releasePattern: 'Gradual decay or held to breath limit'
    },
    traitModifiers: { grit: 40, rasp: 15, projection: 25, airiness: -20, nasality: -25, lowEnd: 45, speed: -20, cleanness: -45 },
    sunoTags: ['low growl', 'death growl', 'deep vocals', 'cookie monster'],
    excludeTags: ['clear lyrics', 'high', 'clean', 'articulate'],
    bestFor: ['Atmospheric sections', 'Doom passages'],
    physicsPrompt: `{SCREAM-PHYSICS: LOW-GROWL}
[Scream_Behavior: Loose cord vibration; Heavy false fold blanket; Expanded throat cavity]
[Scream_Frequencies: 80Hz-250Hz dominant rumble]
[Scream_Dynamics: Soft building attack; Continuous organic rumble; Breath-limit hold]`
  },
  {
    id: 'mid_growl',
    label: 'Mid-Range Growl',
    category: 'guttural',
    description: 'Balanced growl with better lyrical clarity. Workhouse for death metal.',
    physicsProfile: {
      cordEngagement: 'Moderate cord compression with false fold support',
      airflowPattern: 'Rhythmic diaphragm pulses with sustained base',
      resonanceChamber: 'Balanced chest-throat resonance',
      frequencyFocus: '200Hz-600Hz primary with 1kHz clarity peak',
      attackCharacter: 'Moderate attack with clear consonant articulation',
      sustainBehavior: 'Consistent growl with pitch definition',
      releasePattern: 'Controlled fade maintaining texture'
    },
    traitModifiers: { grit: 40, rasp: 25, projection: 30, airiness: -15, nasality: -10, lowEnd: 30, speed: 5, cleanness: -40 },
    sunoTags: ['mid growl', 'death metal', 'growling vocals', 'aggressive vocals'],
    excludeTags: ['clean', 'pop', 'soft', 'whisper'],
    bestFor: ['Verses', 'Storytelling sections'],
    physicsPrompt: `{SCREAM-PHYSICS: MID-GROWL}
[Scream_Behavior: Moderate cord compression; False fold support; Rhythmic diaphragm pulses]
[Scream_Frequencies: 200Hz-600Hz body; 1kHz clarity peak for articulation]
[Scream_Dynamics: Moderate articulation; Consistent pitched growl; Controlled textured fade]`
  },
  {
    id: 'old_school_growl',
    label: 'Old School Growl',
    category: 'guttural',
    description: 'Classic early death metal growl. Less processed, more organic.',
    physicsProfile: {
      cordEngagement: 'Natural cord distortion without extreme technique',
      airflowPattern: 'Raw diaphragm push with organic variation',
      resonanceChamber: 'Natural throat resonance, less manufactured',
      frequencyFocus: '150Hz-500Hz with natural harmonic spread',
      attackCharacter: 'Organic onset, less percussive',
      sustainBehavior: 'Natural variation in texture and pitch',
      releasePattern: 'Organic decay following breath'
    },
    traitModifiers: { grit: 35, rasp: 30, projection: 25, airiness: 5, nasality: 0, lowEnd: 25, speed: 0, cleanness: -35 },
    sunoTags: ['old school death metal', 'classic growl', 'vintage death metal', '90s death metal vocals'],
    excludeTags: ['modern production', 'clean', 'polished', 'processed'],
    bestFor: ['Retro-styled tracks', 'Classic death metal'],
    physicsPrompt: `{SCREAM-PHYSICS: OLD-SCHOOL-GROWL}
[Scream_Behavior: Natural cord distortion; Raw diaphragm push; Organic breath variation]
[Scream_Frequencies: 150Hz-500Hz with natural harmonic spread]
[Scream_Dynamics: Organic attack; naturally varying sustain; Breath-following decay]`
  },

  // -------------------------------------------------------------------------
  // SPECIALTY CATEGORY
  // -------------------------------------------------------------------------
  {
    id: 'tunnel_throat',
    label: 'Tunnel Throat',
    category: 'specialty',
    description: 'Cavernous, hollow-sounding scream. Eerie, atmospheric quality.',
    physicsProfile: {
      cordEngagement: 'Relaxed cord with shaped throat cavity',
      airflowPattern: 'Focused directional airstream',
      resonanceChamber: 'Elongated throat forming acoustic tunnel',
      frequencyFocus: 'Mid-scooped: 200Hz low, 800Hz-1.2kHz hollow center, 3kHz edge',
      attackCharacter: 'Delayed onset with resonance build',
      sustainBehavior: 'Hollow sustained tone with natural reverb quality',
      releasePattern: 'Extended decay mimicking acoustic space'
    },
    traitModifiers: { grit: 20, rasp: 15, projection: 35, airiness: 25, nasality: -15, lowEnd: 10, speed: -15, cleanness: -20 },
    sunoTags: ['tunnel throat', 'cavernous vocals', 'hollow scream', 'atmospheric vocals', 'eerie vocals'],
    excludeTags: ['bright', 'present', 'intimate', 'close-mic'],
    bestFor: ['Atmospheric sections', 'Intros', 'Bridges'],
    physicsPrompt: `{SCREAM-PHYSICS: TUNNEL-THROAT}
[Scream_Behavior: Shaped throat cavity; Focused directional airstream; Acoustic tunnel effect]
[Scream_Frequencies: Mid-scooped profile; 200Hz foundation; 800Hz-1.2kHz hollow center]
[Scream_Dynamics: Resonance-building attack; Hollow sustained tone; Extended decay]`
  },
  {
    id: 'metal_falsetto',
    label: 'Heavy Metal Falsetto',
    category: 'specialty',
    description: 'Screamed falsetto technique. High-pitched but aggressive.',
    physicsProfile: {
      cordEngagement: 'Stretched cord falsetto with added compression',
      airflowPattern: 'High-pressure focused airstream',
      resonanceChamber: 'Head voice resonance with throat reinforcement',
      frequencyFocus: '800Hz-2kHz fundamental, 4kHz-8kHz piercing overtones',
      attackCharacter: 'Sharp onset with immediate pitch lock',
      sustainBehavior: 'Controlled vibrato with aggressive edge',
      releasePattern: 'Can sustain to breath limit or controlled release'
    },
    traitModifiers: { grit: 15, rasp: 20, projection: 45, airiness: 10, nasality: 30, lowEnd: -40, speed: 10, cleanness: -15 },
    sunoTags: ['metal falsetto', 'high scream', 'power metal vocals', 'piercing vocals', 'operatic metal'],
    excludeTags: ['low', 'guttural', 'death metal', 'soft'],
    bestFor: ['Climactic moments', 'Sustained high notes'],
    physicsPrompt: `{SCREAM-PHYSICS: METAL-FALSETTO}
[Scream_Behavior: Stretched cord falsetto; Aggressive compression; Head voice resonance]
[Scream_Frequencies: 800Hz-2kHz fundamental; 4kHz-8kHz piercing shimmer]
[Scream_Dynamics: Sharp attack; Immediate pitch lock; Controlled aggressive vibrato]`
  },
  {
    id: 'distorted_singing',
    label: 'Singing with Distortion',
    category: 'specialty',
    description: 'Melodic singing with controlled vocal distortion. Aggressive texture on melodic lines.',
    physicsProfile: {
      cordEngagement: 'Normal singing phonation with light false fold touch',
      airflowPattern: 'Singing breath support with added pressure',
      resonanceChamber: 'Standard singing resonance with grit overlay',
      frequencyFocus: 'Full melodic spectrum with 2kHz-4kHz grit layer',
      attackCharacter: 'Melodic onset with immediate grit engagement',
      sustainBehavior: 'Pitched sustain with consistent distortion texture',
      releasePattern: 'Melodic resolution with maintained or fading grit'
    },
    traitModifiers: { grit: 30, rasp: 35, projection: 25, airiness: 0, nasality: 10, lowEnd: 5, speed: 0, cleanness: -25 },
    sunoTags: ['distorted singing', 'gritty melodic', 'metalcore cleans', 'aggressive melody', 'textured vocals'],
    excludeTags: ['pure clean', 'smooth', 'soft', 'pristine'],
    bestFor: ['Melodic choruses', 'Emotional sections'],
    physicsPrompt: `{SCREAM-PHYSICS: DISTORTED-SINGING}
[Scream_Behavior: Melodic phonation; Light false fold texture; Standard breath support]
[Scream_Frequencies: Full melodic spectrum; 2kHz-4kHz grit harmonic layer]
[Scream_Dynamics: Melodic attack with grit; Pitched sustain; Consistent distortion]`
  },
  {
    id: 'inhale_scream',
    label: 'Inhale Scream',
    category: 'specialty',
    description: 'Scream produced on the inhale. Creates unique demonic or pig-squeal quality.',
    physicsProfile: {
      cordEngagement: 'Inverted cord tension on reversed airflow',
      airflowPattern: 'Controlled rapid inhale with cord resistance',
      resonanceChamber: 'Reversed resonance pattern, throat constriction',
      frequencyFocus: 'Variable, often high-pitched (1kHz-5kHz) with unstable harmonics',
      attackCharacter: 'Gasping or sucking onset',
      sustainBehavior: 'Limited by lung capacity, inherently shorter phrases',
      releasePattern: 'Abrupt as breath reverses or fades'
    },
    traitModifiers: { grit: 30, rasp: 40, projection: 20, airiness: 35, nasality: 15, lowEnd: -20, speed: 25, cleanness: -45 },
    sunoTags: ['inhale scream', 'pig squeal', 'bree', 'deathcore vocals', 'inverted scream'],
    excludeTags: ['clean', 'melodic', 'sustained', 'warm'],
    bestFor: ['Accents', 'Breakdown punctuation'],
    physicsPrompt: `{SCREAM-PHYSICS: INHALE}
[Scream_Behavior: Inverted cord tension; Reversed airflow; Controlled rapid inhale]
[Scream_Frequencies: Unstable harmonics; 1kHz-5kHz pitch flutter]
[Scream_Dynamics: Gasping/sucking attack; Short breath-capacity sustain; Abrupt reversal]`
  },
  {
    id: 'goblin',
    label: 'The Goblin',
    category: 'specialty',
    description: 'High-pitched, manic, often comedic scream. Chaotic energy.',
    physicsProfile: {
      cordEngagement: 'Tight cord with erratic tension variation',
      airflowPattern: 'Rapid irregular bursts',
      resonanceChamber: 'Nasal-forward with throat constriction',
      frequencyFocus: '1.5kHz-4kHz primary with erratic pitch movement',
      attackCharacter: 'Rapid-fire staccato bursts',
      sustainBehavior: 'Short phrases with pitch instability',
      releasePattern: 'Abrupt cutoffs between bursts'
    },
    traitModifiers: { grit: 25, rasp: 30, projection: 15, airiness: 20, nasality: 40, lowEnd: -35, speed: 45, cleanness: -30 },
    sunoTags: ['goblin vocals', 'manic scream', 'chaotic vocals', 'gremlin', 'erratic vocals'],
    excludeTags: ['smooth', 'controlled', 'low', 'sustained', 'serious'],
    bestFor: ['Chaotic sections', 'Comic relief'],
    physicsPrompt: `{SCREAM-PHYSICS: GOBLIN}
[Scream_Behavior: Tight cord; Erratic tension variation; Rapid irregular breath bursts]
[Scream_Resonance: Nasal-forward with throat constriction]
[Scream_Frequencies: 1.5kHz-4kHz; Constant erratic pitch movement]
[Scream_Dynamics: Staccato burst attacks; Short unstable phrases; Abrupt cutoffs]`
  },
  {
    id: 'kvlt',
    label: 'KVLT Scream',
    category: 'specialty',
    description: 'Black metal banshee shriek. Thin, piercing, often with tremolo.',
    physicsProfile: {
      cordEngagement: 'Extremely tight cord with minimal mass vibration',
      airflowPattern: 'Thin high-pressure stream',
      resonanceChamber: 'Minimal chest, maximum head/nasal resonance',
      frequencyFocus: '2kHz-6kHz piercing fundamental, 8kHz+ ice-pick harmonics',
      attackCharacter: 'Shrieking onset with immediate full intensity',
      sustainBehavior: 'Tremolo variation on sustained notes',
      releasePattern: 'Trailing shriek decay or abrupt stop'
    },
    traitModifiers: { grit: 20, rasp: 45, projection: 30, airiness: 15, nasality: 45, lowEnd: -45, speed: 20, cleanness: -35 },
    sunoTags: ['black metal vocals', 'kvlt', 'shriek', 'banshee', 'piercing scream', 'tremolo vocals'],
    excludeTags: ['low', 'warm', 'full', 'clean', 'death metal'],
    bestFor: ['Black metal passages', 'Atmospheric sections'],
    physicsPrompt: `{SCREAM-PHYSICS: KVLT}
[Scream_Behavior: Minimal vibrating mass; High-pressure thin airstream; Head/nasal resonance]
[Scream_Frequencies: 2kHz-6kHz piercing fundamental; 8kHz+ ice-pick shimmer]
[Scream_Dynamics: Immediate shriek attack; Tremolo-varied sustain; Trailing decay]`
  },

  // -------------------------------------------------------------------------
  // HYBRID CATEGORY
  // -------------------------------------------------------------------------
  {
    id: 'hybrid_scream',
    label: 'Hybrid Scream',
    category: 'hybrid',
    description: 'Combination technique mixing multiple scream types. Modern progressive metal approach.',
    physicsProfile: {
      cordEngagement: 'Dynamic switching between techniques mid-phrase',
      airflowPattern: 'Variable pressure adapting to technique shifts',
      resonanceChamber: 'Full range utilization based on moment',
      frequencyFocus: 'Full spectrum availability, context-dependent',
      attackCharacter: 'Variable - can start with any technique',
      sustainBehavior: 'Morphing texture through phrase',
      releasePattern: 'Context-dependent resolution'
    },
    traitModifiers: { grit: 35, rasp: 30, projection: 30, airiness: 5, nasality: 10, lowEnd: 10, speed: 15, cleanness: -35 },
    sunoTags: ['hybrid vocals', 'progressive metal vocals', 'dynamic screaming', 'versatile extreme vocals'],
    excludeTags: ['monotone', 'static', 'one-dimensional'],
    bestFor: ['Progressive sections', 'Dynamic passages'],
    physicsPrompt: `{SCREAM-PHYSICS: HYBRID}
[Scream_Behavior: Dynamic multi-technique switching; Variable pressure; Fluid resonance]
[Scream_Technique: Combines fry, false chord, and guttural elements]
[Scream_Frequencies: Full spectrum availability; Contextual deployment]
[Scream_Dynamics: Flexible attack; Morphing textured sustain; Contextual resolution]`
  }
];

export function getScreamType(id: string): ScreamType | undefined {
  return SCREAM_TYPES.find(s => s.id === id);
}

export function generateScreamPhysicsBlock(screamType: ScreamType, intensityId: string): string {
  const intensity = SCREAM_INTENSITIES.find(i => i.id === intensityId) || SCREAM_INTENSITIES[2];
  let block = screamType.physicsPrompt;
  
  if (intensityId === 'subtle') {
    block += '\n[Scream_Intensity: SUBTLE; Reduced aggression; Applied as texture layer]';
  } else if (intensityId === 'moderate') {
    block += '\n[Scream_Intensity: MODERATE; Controlled execution; 60% aggression]';
  } else if (intensityId === 'extreme') {
    block += '\n[Scream_Intensity: EXTREME; Maximum aggression; Priority on impact over clarity]';
  }
  
  return block;
}

export const SCREAM_TRAIT_KEYWORDS = {
  grit: {
    high: ['fry scream', 'false chord', 'guttural', 'growl', 'distorted vocals', 'aggressive scream'],
    boost: ['extreme guttural', 'maximum distortion', 'brutal vocals']
  },
  rasp: {
    high: ['inhale scream', 'kvlt', 'black metal shriek', 'banshee', 'tunnel throat'],
    boost: ['extreme rasp', 'sandpaper shriek']
  },
  projection: {
    high: ['metal falsetto', 'power scream', 'arena scream', 'soaring'],
    boost: ['stadium shriek', 'massive projection']
  },
  nasality: {
    high: ['kvlt', 'goblin', 'black metal', 'nasal shriek', 'piercing'],
    boost: ['extreme nasal', 'ice-pick highs']
  },
  lowEnd: {
    high: ['guttural', 'death growl', 'low growl', 'brutal vocals', 'subharmonic'],
    boost: ['floor-shaking guttural', 'sub-bass growl']
  },
  speed: {
    high: ['goblin', 'manic', 'rapid-fire', 'staccato bursts', 'machine-gun delivery'],
    boost: ['frantic', 'unhinged speed']
  },
  cleanness: {
    low: ['scream', 'growl', 'shriek', 'distorted', 'guttural', 'fry', 'false chord'],
    boost: []
  }
};
